import { Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import ModalFilterMasterplan from './ModalFilterMasterplan';
import { useCallback, useState } from 'react';
import { columns as COLUMN_MASTER_PLAN } from './schema';
import { MasterPlanListByGroupItem } from 'types/dacum/masterPlan';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import { pathnames } from 'routes/dacum';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const TableReviewMasterplan = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState(COLUMN_MASTER_PLAN);
  const [filterModalOpened, setFilterModalOpened] = useState(false);
  const [params, setParams] = useState({
    pageSize: 10,
    pageNumber: 1,
    year: undefined as number | undefined,
    search: undefined as string | undefined
  });

  const { data, isPending } = useQuery({
    queryKey: ['masterPlanListByGroup', params],
    queryFn: () =>
      masterPlanService.getMasterPlanListByGroup({
        pageSize: params.pageSize,
        pageNumber: params.pageNumber,
        year: params.year,
        search: params.search
      }),
    placeholderData: keepPreviousData
  });

  const handleChangePage = useCallback((page: number) => {
    setParams((prev) => ({
      ...prev,
      pageNumber: page
    }));
  }, []);

  const handleChangeSize = useCallback((size: number) => {
    setParams((prev) => ({
      ...prev,
      pageSize: size,
      pageNumber: 1
    }));
  }, []);

  const handleView = useCallback(
    (row: MasterPlanListByGroupItem) => {
      const queryParams = new URLSearchParams();
      queryParams.append('jobFamilyGroup', row.jobFamGroup || '');
      const year = row.year || new Date().getFullYear();
      queryParams.append('year', year.toString());
      if (row.endYear) {
        queryParams.append('endYear', row.endYear.toString());
      }
      navigate(`${pathnames.masterPlanDetail}?${queryParams.toString()}`);
    },
    [navigate]
  );

  const handleOpenFilter = useCallback(() => {
    setFilterModalOpened(true);
  }, []);

  const handleExportToExcel = useCallback(() => {
    if (!data?.data || data.data.length === 0) {
      return;
    }

    const exportData = data.data.map((item) => ({
      'Job Family Group': item.jobFamGroup || '-',
      Year: item.year || '-',
      'End Year': item.endYear || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Plan');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = `master-plan-list-${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  }, [data]);

  const tableLoading = isPending;

  return (
    <Card>
      <Stack>
        <Toolbar
          columns={columns}
          showSearch={false}
          showFilter={true}
          showFilterColumn
          showCreateButton={false}
          setColumns={setColumns}
          onCreate={handleExportToExcel}
          onFilter={handleOpenFilter}
        />

        <DataTables
          columns={columns}
          data={data?.data ?? []}
          loading={tableLoading}
          handleView={handleView}
          totalData={data?.totalRecords ?? 0}
          currentPage={params.pageNumber}
          currentSize={params.pageSize}
          handleChangePage={handleChangePage}
          handleChangeSize={handleChangeSize}
          viewText='View Detail'
        />

        <ModalFilterMasterplan
          opened={filterModalOpened}
          onClose={() => setFilterModalOpened(false)}
          onSubmitForm={(data) => {
            setParams((prev) => ({
              ...prev,
              year: data.year,
              pageNumber: 1
            }));
          }}
          defaultValues={{ year: params.year }}
        />
      </Stack>
    </Card>
  );
};

export default TableReviewMasterplan;
